import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Lock, RefreshCw, LogOut, Phone, Mail, Calendar, MessageSquare, Search, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [pass, setPass] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = async (tk) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/appointments`, { headers: { "X-Admin-Token": tk } });
      setItems(res.data);
      setAuthed(true);
      sessionStorage.setItem(KEY, tk);
      setToken(tk);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Invalid passcode");
        setAuthed(false);
        sessionStorage.removeItem(KEY);
      } else {
        toast.error("Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    sessionStorage.removeItem(KEY);
    setAuthed(false);
    setToken("");
    setItems([]);
  };

  const filtered = items.filter((i) => {
    const s = `${i.name} ${i.phone} ${i.email || ""} ${i.concern || ""} ${i.message || ""}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-5">
        <div className="w-full max-w-sm rounded-3xl bg-white border border-black/5 shadow-xl p-8" data-testid="admin-login">
          <div className="w-12 h-12 rounded-2xl bg-[#B9735A]/10 text-[#B9735A] flex items-center justify-center mb-5">
            <Lock size={22} />
          </div>
          <h1 className="font-serif-display text-3xl text-[#1A1C1A]">Admin Access</h1>
          <p className="text-sm text-[#4A4D4A] mt-2 mb-6">Enter the passcode to view appointment enquiries.</p>
          <form onSubmit={(e) => { e.preventDefault(); load(pass); }}>
            <input
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder="Passcode"
              className="w-full bg-[#F6F4EE] rounded-xl px-4 py-3 text-[#1A1C1A] border border-transparent focus:border-[#B9735A] focus:bg-white outline-none transition-colors"
              data-testid="admin-passcode"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading}
              className="mt-4 w-full rounded-full bg-[#B9735A] text-[#FDFBF7] font-medium px-6 py-3 hover:shadow-lg disabled:opacity-70 transition-shadow"
              data-testid="admin-login-btn"
            >
              {loading ? "Checking..." : "Unlock"}
            </button>
          </form>
          <a href="/" className="mt-5 inline-block text-xs text-[#6B7A6A] hover:underline">← Back to website</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-dashboard">
      <header className="border-b border-black/5 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="font-serif-display text-2xl text-[#1A1C1A]">Appointment Enquiries</h1>
            <p className="text-xs text-[#6B7A6A]">{items.length} total submission{items.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(token)} className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-sm text-[#1A1C1A] hover:bg-[#F6F4EE] transition-colors" data-testid="admin-refresh">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-[#1A1C1A] text-white px-4 py-2 text-sm hover:bg-black transition-colors" data-testid="admin-logout">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        <div className="relative mb-6 max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9d9b96]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, phone, concern..."
            className="w-full bg-white border border-black/5 rounded-full pl-11 pr-4 py-3 text-[#1A1C1A] outline-none focus:border-[#B9735A] transition-colors"
            data-testid="admin-search"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-black/10 bg-white p-16 text-center">
            <Inbox className="mx-auto text-[#B9735A]/50" size={36} />
            <p className="mt-4 text-[#4A4D4A]">{items.length === 0 ? "No submissions yet." : "No results match your search."}</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {filtered.map((a) => (
              <div key={a.id} className="rounded-2xl bg-white border border-black/5 p-6 hover:shadow-lg transition-shadow" data-testid="admin-item">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-[#1A1C1A] text-lg">{a.name}</p>
                    {a.concern && <span className="inline-block mt-1 text-xs font-semibold uppercase tracking-wide text-[#B9735A]">{a.concern}</span>}
                  </div>
                  <span className="text-xs text-[#9d9b96] whitespace-nowrap">
                    {new Date(a.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                </div>
                <div className="mt-4 space-y-2 text-sm text-[#4A4D4A]">
                  <a href={`tel:+91${a.phone}`} className="flex items-center gap-2 hover:text-[#B9735A]"><Phone size={14} /> {a.phone}</a>
                  {a.email && <a href={`mailto:${a.email}`} className="flex items-center gap-2 hover:text-[#B9735A] break-all"><Mail size={14} /> {a.email}</a>}
                  {a.preferred_date && <p className="flex items-center gap-2"><Calendar size={14} /> {a.preferred_date}</p>}
                  {a.message && <p className="flex items-start gap-2"><MessageSquare size={14} className="mt-0.5 shrink-0" /> {a.message}</p>}
                </div>
                <a
                  href={`https://wa.me/91${a.phone}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366]/12 text-[#128C4A] text-xs font-semibold px-4 py-2 hover:bg-[#25D366]/20 transition-colors"
                >
                  Reply on WhatsApp
                </a>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
